/** Utility functions for the namespace feature. */

import { ResourceOwner, ResourceOwnerType } from 'src/generated/graphql';
import {
  formatDayMonthYear,
  NOT_AVAILABLE_ID,
} from 'src/common/utils/dateFormatters';
import { buildBindleHref } from 'src/common/utils/bindleLinks';
import type { TranslateFn } from 'src/i18n/shim/useTranslation';

/**
 * Re-exported for the namespace feature's call sites. The implementation lives in
 * `common/` because the access-denied alert needs the identical href, and a second
 * copy of the prod/gamma origin rule would send a refused user to a Bindles
 * environment where the resource does not exist.
 */
export { buildBindleHref };

/**
 * Page for the owner itself, or undefined when the owner is absent or of a kind
 * that has no page to link to.
 */
function resolveOwnerHref(owner?: ResourceOwner | null): string | undefined {
  if (!owner?.ownerId) return undefined;
  const ownerId = owner.ownerId;
  switch (owner.ownerType) {
    case ResourceOwnerType.Team:
      return `https://access.a2z.com/group-management/team/${encodeURIComponent(ownerId)}`;
    case ResourceOwnerType.Individual:
      return `https://phonetool.amazon.com/users/${encodeURIComponent(ownerId)}`;
    default:
      return undefined;
  }
}

/**
 * Link target for a namespace's owner. Falls back to the namespace's Bindle
 * resource, which also shows ownership, so the Owner row is never a dead link.
 */
export function buildOwnerHref(
  owner: ResourceOwner | null | undefined,
  bindleHref?: string,
): string | undefined {
  return resolveOwnerHref(owner) ?? bindleHref;
}

/**
 * Format an epoch-milliseconds timestamp to a human-readable date string
 * ("DD Mon YYYY", e.g. "02 Aug 2026"). All LangHub API timestamps are epoch
 * milliseconds.
 *
 * Shares `formatDayMonthYear`, so dates read identically everywhere, and the
 * `NOT_AVAILABLE_ID` placeholder, so a missing date matches the other namespace
 * table cells in every locale. Unlike the shared formatter, 0 is a real instant
 * here, not an absent value.
 */
export function formatEpochMillisDate(
  t: TranslateFn,
  locale: string,
  epochMillis?: number | null,
): string {
  if (epochMillis == null) return t(NOT_AVAILABLE_ID);
  return formatDayMonthYear(locale, new Date(epochMillis));
}
